// src/pages/Registro.js

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiClient from '../services/api';
import Button from '../components/Button';
import { jwtDecode } from 'jwt-decode';

function Registro() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rol, setRol] = useState('PADRE'); // Preseleccionado PADRE
  const [codigoFamilia, setCodigoFamilia] = useState('');
  const [masterCode, setMasterCode] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState(false);
  const navigate = useNavigate();

  // Redirigir si ya hay sesión
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const userData = jwtDecode(token);
        if (userData.rol === 'PADRE') navigate('/dashboard-padre');
        else if (userData.rol === 'NINO') navigate('/dashboard-nino');
      } catch (e) {
        localStorage.removeItem('accessToken');
      }
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); 

    // Objeto base
    const data = { username, email, password, rol };

    // Añadimos datos específicos según el rol
    if (rol === 'NINO') {
      if (!codigoFamilia) {
        setError('Si eres un niño/a, necesitas un código de familia.');
        return;
      }
      data.codigo_familia = codigoFamilia;
    } else if (rol === 'PADRE') {
      if (!masterCode) {
        setError('Necesitas un código de invitación para crear una cuenta de Padre.');
        return;
      }
      // IMPORTANTE: La clave debe ser 'master_code' (snake_case) para coincidir con Python
      data.master_code = masterCode; 
    }

    try {
      await apiClient.post('/registro/', data);
      setSuccessMessage(true);
    } catch (err) {
      console.error("Error de registro:", err.response?.data);
      const errorData = err.response?.data;

      if (errorData) {
        const getMessage = (field) => Array.isArray(field) ? field[0] : field;

        if (errorData.master_code) setError(getMessage(errorData.master_code));
        else if (errorData.codigo_familia) setError(getMessage(errorData.codigo_familia));
        else if (errorData.username) setError(`Usuario: ${getMessage(errorData.username)}`);
        else if (errorData.email) setError(`Email: ${getMessage(errorData.email)}`);
        else if (errorData.password) setError(`Contraseña: ${getMessage(errorData.password)}`);
        else if (errorData.detail) setError(errorData.detail);
        else setError('Error al registrar. Revisa los datos.');
      } else {
        setError('Error de conexión. Inténtalo de nuevo.');
      }
    }
  };

  if (successMessage) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-fondo p-4">
        <div className="bg-white p-8 rounded-2xl shadow-lg w-full max-w-md text-center">
          <h2 className="text-3xl font-bold text-principal mb-4">¡Casi listo! 📧</h2>
          <p className="text-gray-600 mb-6 text-lg">
            Hemos enviado un correo a <br/>
            <span className="font-bold text-gray-800">{email}</span>
          </p>
          <div className="bg-blue-50 p-4 rounded-lg mb-6 text-sm text-blue-800">
            Revisa tu bandeja de entrada (y spam) y haz clic en el enlace para activar tu cuenta.
          </div>
          <Link to="/login" className="text-acento font-bold hover:underline text-lg">
            Volver al inicio de sesión
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-fondo p-4">
      <form onSubmit={handleSubmit} className="p-8 bg-white rounded-2xl shadow-lg w-full max-w-md">
        <h1 className="text-3xl font-bold text-center text-principal mb-6">✨ Registro Mágico ✨</h1>

        {error && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded" role="alert">
                <p>{error}</p>
            </div>
        )}

        <div className="mb-4">
          <label className="block text-gray-700 font-bold mb-2">Nombre de Usuario</label>
          <input 
            type="text" 
            value={username} 
            onChange={(e) => setUsername(e.target.value)} 
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-principal" 
            placeholder="Elige un nombre único"
            required 
          />
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 font-bold mb-2">Correo Electrónico</label>
          <input 
            type="email" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)} 
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-principal" 
            placeholder="Obligatorio para verificar"
            required
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 font-bold mb-2">Contraseña</label>
          <input 
            type="password" 
            value={password} 
            onChange={(e) => setPassword(e.target.value)} 
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-principal" 
            required 
          />
        </div>

        <div className="mb-6">
          <label className="block text-gray-700 font-bold mb-2">¿Quién eres?</label>
          <select 
            value={rol} 
            onChange={(e) => setRol(e.target.value)} 
            className="w-full px-4 py-2 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-principal"
          >
            <option value="PADRE">Padre / Madre</option>
            <option value="NINO">Niño / Niña</option>
          </select>
        </div>

        {/* CAMPO PARA NIÑOS: CÓDIGO DE FAMILIA */}
        {rol === 'NINO' && (
          <div className="mb-6 p-4 bg-principal bg-opacity-20 rounded-lg animate-fade-in">
            <label className="block text-gray-700 font-bold mb-2">Código de Familia</label>
            <input 
              type="text" 
              value={codigoFamilia} 
              onChange={(e) => setCodigoFamilia(e.target.value)} 
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-principal" 
              placeholder="Pídele este código a tus padres"
            />
            <p className="text-xs text-gray-600 mt-2">Este código te une a tu familia.</p>
          </div>
        )}

        {/* CAMPO PARA PADRES: CÓDIGO MAESTRO */}
        {rol === 'PADRE' && (
          <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200 animate-fade-in">
            <label className="block text-gray-700 font-bold mb-2">🔒 Código de Invitación</label>
            <input 
              type="password" 
              value={masterCode} 
              onChange={(e) => setMasterCode(e.target.value)} 
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:outline-none" 
              placeholder="Código secreto del administrador"
            />
            <p className="text-xs text-gray-600 mt-2">Necesitas un código especial para crear una nueva familia.</p>
          </div>
        )}

        <Button type="submit" variant="accent" className="w-full py-3 text-lg shadow-md">
          ¡Crear Cuenta!
        </Button>

        <p className="text-center text-sm text-gray-600 mt-6">
          ¿Ya tienes una cuenta?{' '}
          <Link to="/login" className="font-bold text-principal hover:underline">
            Inicia sesión aquí
          </Link>
        </p>
      </form>
    </div>
  );
}

export default Registro;